import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonProps } from "@insite/mobius/Button";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Tooltip from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

const enum fields {
    tooltipText = "tooltipText",
    image = "image",
    sampleResultUrl = "sampleResultUrl",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.tooltipText]: string;
        [fields.image]: string;
        [fields.sampleResultUrl]: string;
    };
}

export interface MeetYourHerbsSearchInputStyles {
    inputWrapper?: InjectableCss;
    input?: TextFieldPresentationProps;
    searchBtn?: ButtonProps;
    searchLink?: LinkPresentationProps;
    disclaimer?: InjectableCss;
    tooltipWrapper?: InjectableCss;
    tooltipContent?: InjectableCss;
    tooltipImage?: LazyImageProps;
    tooltipText?: TypographyProps;
    errMsg?: TypographyPresentationProps;
    toolTipLabel?: TypographyPresentationProps;
}

const styles: MeetYourHerbsSearchInputStyles = {
    inputWrapper: {
        css: css`
            position: relative;
        `,
    },
    input: {
        sizeVariant: "default",
        border: "underline",
        cssOverrides: {
            formInputWrapper: css`
                > [data-test-selector="MeetYourHerbsSearchInputTextField"] {
                    border-radius: 8px !important;
                    height: 46px;
                    border: 1px solid #d1412440;
                    padding: 0 100px 0 23px !important;
                }
            `,
            inputSelect: css`
                &:focus {
                    outline-color: #09f;
                    outline-style: solid;
                    outline-width: 2px;
                    border: none;
                }
            `,
        },
    },
    searchBtn: {
        css: css`
            position: absolute;
            right: 2px;
            top: 2px;
            height: 41px;
            border-bottom-left-radius: 0;
            border-top-left-radius: 0;
        `,
    },
    searchLink: {
        css: css`
            display: none;
        `,
    },
    disclaimer: {
        css: css`
            display: flex;
            margin-top: 10px;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            color: ${getColor("primary.contrast")};
        `,
    },
    tooltipWrapper: {
        css: css`
            position: relative;
            display: flex;
            flex-direction: row;
            align-items: stretch;
            button {
                padding: 0;
                &:focus {
                    outline: none;
                }
                span[class^="IconWrapper"] {
                    height: 20px;
                    width: 20px;
                    position: unset;
                    svg {
                        fill: ${getColor("primary.contrast")};
                        height: 20px;
                        width: 20px;
                    }
                }
                & ~ div {
                    display: none;
                }
            }
            div[class^="TooltipWrapper"] {
                position: unset;
                display: inline-block;
                &:hover ~ div[data-test-selector="tooltip-content"] {
                    display: flex;
                }
            }
        `,
    },
    tooltipContent: {
        css: css`
            display: none;
            width: 268px;
            height: 94px;
            overflow: hidden;
            flex-direction: row;
            background: #183028;
            padding: 5px;
            border-radius: 8px;
            position: absolute;
            top: 25px;
        `,
    },
    tooltipImage: {
        css: css`
            width: 84px;
            min-width: 84px;
            height: 84px;
            border: 2px solid ${getColor("primary.contrast")};
            border-radius: 4px;
            img {
                height: 100%;
            }
        `,
    },
    tooltipText: {
        size: 14,
        css: css`
            margin-left: 10px;
        `,
    },
    toolTipLabel: {
        css: css`
            cursor: pointer;
            &:hover ~ div[data-test-selector="tooltip-content"] {
                display: flex;
            }
        `,
    },
    errMsg: {
        css: css`
            color: rgb(191, 13, 62);
            font-family: "LatoBold";
        `,
    },
};

const SEARCH_TEXT_ERR_MSG = "Please enter herb id.";
type Props = OwnProps & HasHistory;

const MeetYourHerbsSearchInput: FC<Props> = ({ fields, history }) => {
    const [searchText, setSearchText] = useState("");
    const [searchTextError, setSearchTextError] = useState("");
    const linkRef = React.createRef();

    const handleSearch = () => {
        if (!searchText) {
            setSearchTextError(SEARCH_TEXT_ERR_MSG);
        } else if (searchText && window && !searchTextError) {
            setSearchTextError("");
            const link = document.getElementById("meet-your-herbs-results-go-link");
            if (link) {
                link.click();
            }
        }
    };
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            handleSearch();
        }
    };
    const onChangeSearchText = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e) {
            return false;
        }
        setSearchText(e.target.value);
        setSearchTextError("");
    };

    return (
        <>
            <StyledWrapper {...styles.inputWrapper}>
                <TextField
                    {...styles.input}
                    value={searchText}
                    onChange={onChangeSearchText}
                    placeholder={translate("Enter the Herb ID")}
                    required
                    data-test-selector="MeetYourHerbsSearchInputTextField"
                    onKeyDown={handleKeyDown}
                />
                <Button {...styles.searchBtn} onClick={handleSearch}>
                    {translate("Go")}
                </Button>
                <Link
                    id="meet-your-herbs-results-go-link"
                    href={`https://www.gaiaherbs.com/pages/meet-your-herbs-results?id=${searchText}`}
                    target="_new"
                    {...styles.searchLink}
                >
                    {translate("Go")}
                </Link>
            </StyledWrapper>
            {searchTextError && (
                <Typography as="p" {...styles.errMsg}>
                    {translate(SEARCH_TEXT_ERR_MSG)}
                </Typography>
            )}
            <StyledWrapper {...styles.disclaimer}>
                <StyledWrapper {...styles.tooltipWrapper}>
                    <Tooltip text="Your Product ID can be found on the back of your product"></Tooltip>
                    <Typography {...styles.toolTipLabel}>Where to find ID?</Typography>
                    <StyledWrapper {...styles.tooltipContent} data-test-selector="tooltip-content">
                        <LazyImage src={fields.image} altText="thumbnail" {...styles.tooltipImage} />
                        <Typography {...styles.tooltipText}>{fields.tooltipText}</Typography>
                    </StyledWrapper>
                </StyledWrapper>
                {fields.sampleResultUrl && (
                    <Link target="_blank" href={fields.sampleResultUrl} color="#fff">
                        <Typography>View sample test result</Typography>
                    </Link>
                )}
            </StyledWrapper>
        </>
    );
};

const meetYourHerbsSearchInput: WidgetModule = {
    component: withHistory(MeetYourHerbsSearchInput),
    definition: {
        group: "Basic",
        displayName: "Meet Your Herbs Search Input",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.image,
                displayName: "Tooltip Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },

            {
                fieldType: "General",
                name: fields.tooltipText,
                displayName: "ToolTip Text",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.sampleResultUrl,
                displayName: "Sample Result URL",
                editorTemplate: "TextField",
                defaultValue: "",
            },
        ],
    },
};

export default meetYourHerbsSearchInput;
